import { useRef, useState } from "react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { UploadCloud, Download, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function BulkImportDialog({ onImported }) {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const inputRef = useRef();

    const reset = () => { setFile(null); setResult(null); };

    const submit = async () => {
        if (!file) return toast.error("Choose a CSV first");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/customers/import", fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            setResult(data);
            toast.success(`Imported ${data.created} leads`);
            onImported && onImported();
        } catch (err) {
            toast.error(formatDetail(err.response?.data?.detail) || "Upload failed");
        } finally { setBusy(false); }
    };

    const downloadTemplate = async () => {
        try {
            const res = await api.get("/customers/import/template.csv", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = "komma-leads-template.csv"; a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("Template unavailable"); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
                <Button variant="outline" className="border-komma-gold text-komma-navy hover:bg-komma-gold/10" data-testid="bulk-import-btn">
                    <UploadCloud size={16} className="mr-2" /> Bulk Import
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-serif text-komma-navy flex items-center gap-2">
                        <Sparkles size={16} className="text-komma-gold" /> Import Leads from CSV
                    </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-gray-600">
                    Upload a CSV with columns <span className="font-mono text-xs text-komma-navy">name, phone, email, occupation, monthly_income, address, pan, notes</span>. Duplicates (by phone) are skipped.
                </p>
                <Button variant="ghost" onClick={downloadTemplate} className="justify-start text-komma-navy hover:bg-komma-gold/10" data-testid="download-template">
                    <Download size={14} className="mr-2 text-komma-gold" /> Download template
                </Button>

                <div
                    onClick={() => inputRef.current?.click()}
                    className="border-2 border-dashed border-komma-navy/20 rounded-lg p-6 text-center cursor-pointer hover:border-komma-gold transition-colors"
                    data-testid="import-dropzone">
                    {file ? (
                        <div>
                            <p className="font-serif text-lg text-komma-navy">{file.name}</p>
                            <p className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB · click to change</p>
                        </div>
                    ) : (
                        <>
                            <UploadCloud className="mx-auto text-komma-gold" size={32} />
                            <p className="mt-2 text-sm text-komma-navy">Click to choose CSV</p>
                            <p className="text-xs text-gray-500 mt-1">Max 2 MB</p>
                        </>
                    )}
                    <input ref={inputRef} type="file" accept=".csv,text/csv" hidden
                        onChange={(e) => setFile(e.target.files?.[0])} data-testid="import-file-input" />
                </div>

                {result && (
                    <div className="rounded-md border border-gray-100 p-4 bg-komma-cream/50" data-testid="import-result">
                        <div className="grid grid-cols-3 gap-3 text-center">
                            <StatBlock icon={CheckCircle2} label="Imported" value={result.created} accent="emerald" />
                            <StatBlock icon={Sparkles} label="Skipped" value={result.skipped} accent="gold" />
                            <StatBlock icon={AlertTriangle} label="Errors" value={result.total_errors ?? result.errors?.length ?? 0} accent="red" />
                        </div>
                        {result.errors?.length > 0 && (
                            <div className="mt-3 max-h-32 overflow-y-auto text-xs text-red-700">
                                {result.errors.map((e, i) => (
                                    <p key={i}>Line {e.line}: {e.reason}</p>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <Button onClick={submit} disabled={busy || !file}
                    className="w-full bg-komma-navy hover:bg-komma-navy-light" data-testid="import-submit">
                    {busy ? "Importing…" : "Upload & Import"}
                </Button>
            </DialogContent>
        </Dialog>
    );
}

function StatBlock({ icon: Icon, label, value, accent }) {
    const colors = { emerald: "text-emerald-700", gold: "text-komma-gold-dark", red: "text-red-700" };
    return (
        <div>
            <Icon size={16} className={`mx-auto ${colors[accent]}`} />
            <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">{label}</p>
            <p className={`font-serif text-2xl ${colors[accent]}`}>{value}</p>
        </div>
    );
}
